import { TabType } from "../components/CreateNoteModal/AITemplateModal/types";
import { Template } from "../service/templates.service";

export type TProceduralCode = {
  id: number;
  code: string;
  short_description: string;
  rationale: string;
  additional_fields?: Record<string, any> | null;
};

export type TDiagnosticCode = {
  id: number;
  code: string;
  short_description: string;
  rationale: string;
  additional_fields?: Record<string, any> | null;
};

export type TNotes = {
  client_id?: number;
  name?: string;
  summary?: string;
  audio_id?: number | string;
  id: number;
  title: string;
  note_type: number;
  note_type_info?: {
    pk: number;
    id: number;
    name: string;
    document: string;
    finished_generating: boolean;
    settings: { is_addon: boolean; is_cpt_codes: boolean };
  };
  uploaded_at: string;
  assessment: string;
  error_status: number;
  category_type: number;
  language_type: number;
  status: number;
  is_read: boolean;
  stats_transcribe_start_at: string;
  stats_generate_note_start_at: string;
  modality_type: number;
  duration_seconds: number;
  subject: string;
  intake_note: string;
  eap_intake_note: string;
  psych_intake_note: string;
  psych_followup_note?: string;
  data: string;
  document?: string;
  emdr_note: string;
  avs_email: string;
  objective: string;
  plan: string;
  gender_type: number;
  client?: number;
  location_type?: number;
  duration_type?: number | null;
  duration_addon_type?: number;
  client_name?: string;
  client_uuid?: string | null;
  session_insights?: string;
  stats_generate_note_end_at: string;
  treatment_plan?: string;
  treatment_plan_generating?: boolean;
  sol_psych_intake_note?: string;
  sol_psych_followup_note?: string;
  sol_therapy_intake_note?: string;
  sol_therapy_followup_note?: string;
  psychotherapy_minutes?: number;
  psychotherapy_minutes_explanation?: string;
  suggested_cpt_type?: number;
  suggested_cpt_code_explanation?: string;
  is_incapture?: boolean;
  system_metadata?: {
    [key: string]: any;
  };
  procedural_codes: TProceduralCode[] | null;
  procedural_code_status: ProceduralCodeStatus;
  diagnostic_codes: TDiagnosticCode[] | null;
  diagnostic_code_status: DiagnosticCodeStatus;
  stats_generate_coding_end_at?: string | null;
  note_last_edited_at?: string | null;
  has_transcript?: boolean;
  is_processing_timed_out?: boolean;
  is_owned?: boolean;
};

export type Tdsm = {
  id: number;
  code: string;
  name: string;
};

export type TClient = {
  id: number;
  first_name: string;
  last_name: string;
};

export type TRecordingDetailsType = {
  title: string;
  note_type?: number | null;
  category_type: number;
  modality_type: string;
  gender_type: number;
  language_type: number;
  location_type?: number;
  duration_type?: number | null;
  duration_addon_type?: number;
  client_id?: number | null;
  // Carried alongside client_id so the in-progress recording header can show the patient's name and
  // navigate by uuid even when the client isn't on the loaded page (no in-memory list dependency).
  client_uuid?: string | null;
  client_name?: string | null;
  description?: string;
  fileList?: any[];
  is_template?: boolean;
  template_id?: number;
  current_recording_id?: number;
};

export interface AppState {
  isCreateClientDrawerVisible: boolean;
  isCustomiseSectionVisible: boolean;
  selectedClient: any;
  uploadInProgress: boolean;
  isEditNote: boolean;
  attachDetailsFor: number | null;
  isCreateModalVisible: boolean;
  /** Why the drawer is opening. "manual": Start Encounter after the clinician turned Quick Start's pick down (×), so the schedule pick pre-selects nobody. */
  createModalIntent: "manual" | null;
  isAITemplateModalVisible: boolean;
  isSubmissionInProgress: boolean;
  selectedNoteForEditing: any;
  isNotesViewVisible: boolean;
  uploadType: number;
  showRecordingView: boolean;
  loadingNotes: boolean;
  currentPage: number;
  isDemoPath: boolean;
  isMobilePath: boolean;
  openShare: boolean;
  showDemoSignupModal: boolean;
  addClientView: boolean;
  audioDevices: any[];
  customTemplateSelectedTab: TabType;
  demoState: {
    isClientModalOpen: boolean;
    isNoteModalOpen: boolean;
    clientForm: any;
    recordingDetails: any;
  };
  templateOpenedFromNoteModal: boolean;
  storedTemplateData: {
    id: number;
    pk?: number;
    name: string;
    document: string;
    prompt?: string;
    isFromTemplate?: boolean;
    appendInstructions?: string;
    focusInstructions?: boolean;
  } | null;
  templates: Template[];
  selectedView: string;
  showRestrictionModal: boolean;
  showSettingsModal: boolean;
  shareClientModalVisible: boolean;
  shareClientModalClient: TClientForm | null;
}

export type TAudioFileDocument = {
  id: number;
  filename: string;
  file_path: string;
  file_size: number;
  uploaded_at: string;
};

export type TClientForm = {
  id?: number;
  uuid?: string;
  name: string;
  category_type: number;
  gender_type: number;
  language_type: number;
  diagnosis_ids: number[];
  refer_type?: number;
  age?: number | string;
  mrn?: string | null;
  /**
   * Whether this patient's MRN resolves to this patient in the EHR. Empty/absent
   * means never checked. Anything other than "verified" means note transfer will
   * refuse — the MRN does not identify this patient in the EHR.
   */
  ehr_verification?: "verified" | "not_found" | "ambiguous" | "mismatch" | "" | null;
  ehr_verified_at?: string | null;
  date_of_birth?: string | null;
  context?: string;
  progress_summary?: TProgressSummary;
  progress_summary_last_updated_at?: string;
  diagnoses?: Tdsm[];
  is_owned?: boolean;
  can_edit?: boolean;
  owner_name?: string;
  owner_email?: string;
  /** Appointment start from the last EHR schedule pull (ISO instant); null when not on a pulled schedule. */
  scheduled_at?: string | null;
};

export type TClientShare = {
  id: number;
  client: number;
  shared_with: number;
  shared_with_name: string;
  shared_with_email: string;
  created_at: string;
};

export type TClientAutoShare = {
  id: number;
  owner: number;
  shared_with: number;
  shared_with_name: string;
  shared_with_email: string;
  created_by: number | null;
  created_at: string;
  in_group: boolean;
};

export type TAutoShareEligibleMember = {
  id: number;
  name: string;
  email: string;
};

export type TAutoShareResponse = {
  owner: { id: number; name: string; email: string };
  client_count: number;
  rules: TClientAutoShare[];
  eligible_members: TAutoShareEligibleMember[];
  created?: number;
  already_active?: number[];
  invalid_users?: number[];
  backfill_queued?: boolean;
};

export type TClientListResponse = {
  owned: TClientForm[];
  shared: TClientForm[];
};

// Server-side pagination + search over /client/list/ (mode-switch: present params => paginated).
export type TClientScope = "owned" | "shared" | "all";

export type TClientPageParams = {
  search?: string;
  scope?: TClientScope;
  page_size?: number;
  offset?: number;
  /** "recent" = newest-first (id DESC); "schedule" = today's day sheet (needs the window
   *  below); absent = alphabetical (name, id). */
  ordering?: "recent" | "schedule";
  /** With ordering=schedule: the clinic's local day (stored timezone, browser as fallback) as ISO
   *  instants, `[from, to)` — see `todayScheduleWindow`. */
  schedule_from?: string;
  schedule_to?: string;
};

export type TClientPageResponse = {
  results: TClientForm[];
  hasMore: boolean;
  next_offset: number | null;
};

export type TProgressSummary = {
  next_steps: string;
  primary_concern: string;
  session_summary: string;
};

export enum ProceduralCodeStatus {
  PENDING = 0,
  COMPLETE = 1,
  NOT_PROCESSED = 2,
  FAILED = 3,
}

export enum DiagnosticCodeStatus {
  PENDING = 0,
  COMPLETE = 1,
  NOT_PROCESSED = 2,
  FAILED = 3,
}

export enum ModalityMode {
  audio = 0,
  dictate = 1,
  write = 2,
  upload = 3,
}

// TypeScript interfaces for onboarding form data
export interface OnboardingFormData {
  practice_area: string; // code
  specialty: string; // code
  referral_source: string;
  ehr: string;
  work_as: string;
}

export interface OnboardingData {
  specialty?: string; // for BE extraction compatibility
  referral_source?: string;
  ehr?: string;
  work_as?: string;
  completed?: boolean;
  start_time?: string;
  timezone?: string;
}

// EHR Transfer Types
export type EHRTransferStatus =
  | "not_started"
  | "pending"
  | "logging_in"
  | "awaiting_mfa"
  | "patient_lookup"
  | "awaiting_appointment"
  | "awaiting_note_type"
  | "template_mapping"
  | "writing_note"
  | "completed"
  | "failed"
  | "cancelled";

export type AppointmentOption = {
  id: string;
  date: string;
  time: string;
  visit_type: string;
  provider?: string;
  status?: string;
  location?: string;
  department?: string; // EHR department/clinic the appointment belongs to (multi-dept practices)
  duration?: string;
  kind?: string; // "department" when this picker is a department selection
  [key: string]: string | undefined;
};

// The picked appointment as the button/banner names it — the picker's fields
// plus a clinic-local day label computed server-side.
export type SelectedAppointmentSummary = {
  id: string;
  date: string;
  time: string;
  visit_type: string;
  provider: string;
  day_label: string;
  time_label?: string;
};

export type TransferJob = {
  id: string;
  status: EHRTransferStatus;
  patient_name: string;
  error_message?: string;
  selected_appointment_summary?: SelectedAppointmentSummary | null;
  requires_appointment_selection: boolean;
  requires_note_type_selection?: boolean;
  requires_mfa_code?: boolean;
  mfa_options?: Array<{ id: string; label: string }>;
  appointment_options?: AppointmentOption[];
  created_at?: string;
  updated_at?: string;
  completed_at?: string;
  // Present when fetched via the /active/ endpoint — used by the global banner
  // to render copy + deep-link without needing extra round-trips.
  audio_file_id?: number | null;
  audio_file_title?: string;
  client_uuid?: string | null;
  client_name?: string;
  ehr_name?: string;
};

export type ActiveTransferResponse = {
  success: boolean;
  // Every in-flight (+ recently-finished) transfer for the user. Multiple
  // transfers can run at once, so the banner/buttons read the full list.
  jobs?: TransferJob[];
  // Back-compat: the first/most-recent job. Older clients read this; new code
  // should prefer `jobs`.
  job: TransferJob | null;
};

export type EHRCredentialCheckResponse = {
  has_credential: boolean;
  credential_type?: "user" | "group";
  ehr_provider?: string;
  ehr_provider_display?: string;
};

export type StartEHRTransferResponse = {
  success: boolean;
  job_id?: string;
  status?: EHRTransferStatus;
  task_id?: string;
  error?: string;
  // Error code on failure; on success may carry "appointment_gone" (the
  // clicked appointment left the schedule, the picker will open instead).
  code?: string;
  // The appointment the kickoff selected, when it auto-selected one.
  appointment_id?: string;
};

// ---- One-click transfer target (GET /rpa/agent/transfer/target/<audio_id>/)

// `not_applicable` (always `enabled: false`), or any fetch error = render today's
// plain button. Every other state still lets the primary click through.
export type TransferTargetState = "ready" | "loading" | "empty" | "unavailable" | "not_applicable";

export type TransferTargetNoDefaultReason = "too_far" | "ambiguous" | "none_eligible";

// One schedule row as the backend annotates it. Dates are pre-parsed server-side
// in the clinic timezone; the FE renders labels and never parses EHR strings.
export type TransferTargetOption = {
  id: string;
  encounter_id: string;
  date: string;
  time: string;
  visit_type: string;
  provider?: string;
  location?: string;
  department?: string;
  status?: string;
  starts_at: string | null;
  local_date: string | null;
  day_label: string;
  // "2:30 PM", or "" when the row carries no real time.
  time_label?: string;
  distance_days: number | null;
  transferred_note_id: number | null;
  transferred_at: string | null;
  signed: boolean;
  compile: TransferReadinessLane;
};

// Where a transfer was started from, and how its appointment was chosen. Rides
// on the start request (analytics only — the server never trusts it for a
// write) so `transfer_started` / `transfer_finished` can be joined to it.
export type TransferSurface = "note_button" | "superdoc_card";
export type TransferClickSource = "suggested" | "chosen" | "menu" | "retry" | "legacy";
export type TransferClickMeta = {
  surface: TransferSurface;
  source: TransferClickSource;
  suggested_id?: string | null;
};

export type TransferTarget = {
  enabled: boolean;
  state: TransferTargetState;
  reason: string | null;
  suggested_id: string | null;
  no_default_reason: TransferTargetNoDefaultReason | null;
  options: TransferTargetOption[];
  // Epoch seconds the schedule was read from the EHR.
  fetched_at: number | null;
  mrn: string | null;
  // A re-read is queued or running while these rows are served; poll again.
  refreshing?: boolean;
  // The appointment the clinician chose for this note (Superdoc's Encounter
  // field); every surface shows it instead of suggested_id when set.
  selected_id?: string | null;
};

// One lane of the per-note transfer readiness checklist. "absent" = no lane
// ever stamped (or it expired): gates nothing, like a missing block.
export type TransferReadinessLane = "pending" | "ready" | "failed" | "absent";

// Rides the existing status poll. `null`/missing means the backend doesn't
// compute readiness for this note — the FE gates nothing.
export type TransferReadiness = {
  llm: TransferReadinessLane;
  session: TransferReadinessLane;
};

export type EHRTransferStatusResponse = {
  success: boolean;
  job?: TransferJob | null;
  status?: EHRTransferStatus;
  error?: string;
  transfer_readiness?: TransferReadiness | null;
};

// EHR patient sync ("Sync" button). `busy` = another sync for the clinic holds
// the credential; `refused` = the gate rejected this user between click and run.
export type EHRSyncStatus = "running" | "ok" | "busy" | "refused" | "error";

// One click's progress blob. The `*_ts` fields are epoch SECONDS.
export type EHRSyncRun = {
  status: EHRSyncStatus;
  created: number;
  updated: number;
  skipped: number;
  notes_seeded: number;
  error?: string | null;
  started_ts?: number;
  deadline_ts?: number;
  finished_ts?: number | null;
};

export type StartEHRSyncResponse = {
  success: boolean;
  run_id: string;
  run: EHRSyncRun | null;
};

export type EHRSyncRunResponse = {
  success: boolean;
  run: EHRSyncRun;
};
